import React, { useEffect, useState } from 'react';
import { Flex, Skeleton } from 'antd';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseTag from 'components/atoms/base-tag';
import BaseTextArea from 'components/atoms/base-textarea';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import { useEdit } from 'infrastructure/providers/edit-provider';
import { useUpdateMedicationDetail } from 'infrastructure/hooks/medications';

import {
  AVAILABLE_TAG_COLOR,
  UNAVAILABLE_TAG_COLOR,
} from '../medications-table';
import { medicationSummaryValidationSchema } from './validation-schema';
import MedicationBlock from './medication-block';
import { revertMedicationDetailTextField } from './utils';
import s from './styles.module.scss';

import type { IAdminMedicationDetails } from 'infrastructure/interfaces';
import type { Nullable } from 'infrastructure/types';

const formKey = 'MedicationSummaryForm';

interface MedicationSummaryProps {
  medication?: Nullable<IAdminMedicationDetails>;
  isLoading?: boolean;
  readOnly?: boolean;
}

const MedicationSummary: React.FC<MedicationSummaryProps> = ({
  medication,
  isLoading = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { canEdit, setEdit } = useEdit();
  const { updateMedication, isLoading: isUpdating } =
    useUpdateMedicationDetail();

  const formik = useFormik({
    initialValues: medication || {
      name: '',
      manufacturer: '',
      otc: false,
      papAvailable: false,
      couponDescription: '',
    },
    onSubmit: async (values) => {
      if (!medication?.guid) return;

      updateMedication(
        {
          medicationGuid: medication.guid,
          payload: {
            otc: values.otc,
            manufacturer: values.manufacturer || null,
            couponDescription: values.couponDescription || null,
          },
        },
        {
          onSuccess: () => {
            formik.resetForm({ values });
            setIsEdit(false);
            setEdit(false);
          },
        },
      );
    },
    validationSchema: medicationSummaryValidationSchema(),
  });

  const onEdit = () => {
    const editableValues = revertMedicationDetailTextField(
      formik.values as IAdminMedicationDetails,
    );
    if (editableValues) {
      formik.setValues(editableValues);
    }
    setIsEdit(true);
    setEdit(true, formKey);
  };

  const onSave = () => {
    formik.submitForm();
  };

  const onCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEdit(false);
  };

  useEffect(() => {
    if (medication) {
      formik.resetForm({ values: medication });
    }
  }, [medication]);

  return (
    <MedicationBlock
      title={t('labels.medicationInformation')}
      isEdit={isEdit}
      onEdit={onEdit}
      onSave={onSave}
      onCancel={onCancel}
      canEdit={!readOnly}
      loading={isLoading || isUpdating}
      disabled={!canEdit(formKey)}
      disabledSave={!formik.isValid}
    >
      <BaseForm formik={formik} plaintext={!isEdit} readonly={!isEdit}>
        <Flex gap="2rem">
          <div className={s['summary-grid']}>
            <BaseFormItem
              name="name"
              label={t('labels.medicationName')}
              className={s['compact-textarea']}
            >
              {isLoading ? (
                <Skeleton.Input
                  active
                  size="small"
                  className={s['skeleton-long']}
                />
              ) : (
                <BaseTextArea rows={1} maxRows={3} disabled={isEdit} />
              )}
            </BaseFormItem>
            <BaseFormItem
              name="manufacturer"
              label={t('labels.manufacturer')}
              className={s['compact-textarea']}
              validateOnChange
            >
              {isLoading ? (
                <Skeleton.Input
                  active
                  size="small"
                  className={s['skeleton-medium']}
                />
              ) : (
                <BaseTextArea rows={1} maxRows={3} />
              )}
            </BaseFormItem>
            <BaseFormItem name="otc" label={t('labels.otc')} validateOnChange>
              {isLoading ? (
                <Skeleton.Input active size="small" />
              ) : (
                <BooleanSelectBox
                  trueLabel={t('labels.yes')}
                  falseLabel={t('labels.no')}
                />
              )}
            </BaseFormItem>
          </div>
          <BaseFormItem
            name="papAvailable"
            label={t('labels.papAvailable')}
            className={s['pap-available-field']}
          >
            <>
              {isLoading ? (
                <Skeleton.Input active size="small" />
              ) : (
                <div className={isEdit ? s['disabled-field'] : undefined}>
                  <BaseTag
                    label={
                      formik.values.papAvailable
                        ? t('labels.available')
                        : t('labels.notAvailable')
                    }
                    color={
                      formik.values.papAvailable
                        ? AVAILABLE_TAG_COLOR
                        : UNAVAILABLE_TAG_COLOR
                    }
                  />
                </div>
              )}
            </>
          </BaseFormItem>
        </Flex>
        <BaseFormItem
          name="couponDescription"
          label={t('labels.couponDescription')}
          className={classNames(s['notes-block'], s['compact-textarea'])}
          validateOnChange
        >
          {isLoading ? (
            <Skeleton.Input
              active
              size="small"
              className={s['skeleton-long']}
            />
          ) : (
            <BaseTextArea rows={1} maxRows={2} />
          )}
        </BaseFormItem>
      </BaseForm>
    </MedicationBlock>
  );
};

export default MedicationSummary;
